import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tab,
    Tabs,
    TextField,
    Box,
    IconButton,
    CardMedia,
    Input,
    Autocomplete,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ReactPlayer from "react-player";
import { useCreateWorkout, useUpdateWorkout, useGetWorkout } from "../../api/hooks/useWorkout";
import { workoutService } from "../../api/services/workoutService";
import { uploadService } from "../../api/services/uploadService";
import { useGetSettings } from "../../api/hooks/useSettings";
import { WORKOUT_ATTRIBUTES, WORKOUT_DURATION, DIFFICULTY_LEVEL, VISIBILITY_SCOPE } from "../../constants/settings";
import { useGetCategories } from "../../api/hooks/useCategory";
import { showError, showSuccess } from "../../helpers/toast";
import TabPanel from "./TabPanel";
import EditText from "../../components/editText";

const extractMuxPlaybackId = (url: string) => {
    const rawValue = (url || "").trim();
    if (!rawValue) return "";

    const match = rawValue.match(/stream\.mux\.com\/([^./?]+)\.m3u8/i);
    if (match?.[1]) return match[1];

    // Backward compatibility: API may already return plain playbackId.
    if (!/^https?:\/\//i.test(rawValue)) return rawValue;

    return "";
};

const normalizeDifficultyCode = (value: any): string | null => {
    const code = Number(value);
    if (!Number.isFinite(code)) return null;
    if (code === DIFFICULTY_LEVEL.BEGGINER) return String(DIFFICULTY_LEVEL.BEGGINER);
    if (code === DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE) return String(DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE);
    if (code === DIFFICULTY_LEVEL.INTERMEDIATE) return String(DIFFICULTY_LEVEL.INTERMEDIATE);
    if (code === DIFFICULTY_LEVEL.ADVANCED) return String(DIFFICULTY_LEVEL.ADVANCED);
    return null;
};

const EditWorkout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const workoutId =
        (location.state as any)?.workoutId || searchParams.get("workoutId") || "";
    const duplicateFromId =
        (location.state as any)?.duplicateFrom || searchParams.get("duplicateFrom") || "";
    const isEditMode = !!workoutId;
    const isDuplicateMode = !isEditMode && !!duplicateFromId;
    const sourceWorkoutId = isEditMode ? workoutId : duplicateFromId;
    const [tabIndex, setTabIndex] = useState(0);

    const [image, setImage] = useState<string>("");
    const [fileName, setFileName] = useState<string>("");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [title, setTitle] = useState("");
    const [refCode, setRefCode] = useState("");
    const [durationEnum, setDurationEnum] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [subCategoryId, setSubCategoryId] = useState("");
    const [difficultyLevel, setDifficultyLevel] = useState<string[]>([]);
    const [visibilityScope, setVisibilityScope] = useState("");
    const [equipment, setEquipment] = useState<string[]>([]);
    const [muscleGroup, setMuscleGroup] = useState<string[]>([]);
    const [trainingStyle, setTrainingStyle] = useState<string[]>([]);
    const [goalOutcome, setGoalOutcome] = useState<string[]>([]);
    const [shortDescription, setShortDescription] = useState("");
    const [description, setDescription] = useState("");
    const [playbackId, setPlaybackId] = useState("");
    const [videoUrl, setVideoUrl] = useState("");
    const [durationMins, setDurationMins] = useState("");
    const [durationSecs, setDurationSecs] = useState("");
    const [productLinks, setProductLinks] = useState<Array<{ name: string; url: string }>>([]);
    const [exerciseContent, setExerciseContent] = useState("");
    const [isFeatured, setIsFeatured] = useState(false);
    const [isPublished, setIsPublished] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [programRefId, setProgramRefId] = useState<string[]>([]);
    const [selectedProgramsData, setSelectedProgramsData] = useState<Array<{ _id: string; title?: string }>>([]);
    const [programSearch, setProgramSearch] = useState("");
    const [debouncedProgramSearch, setDebouncedProgramSearch] = useState("");
    const [programOptions, setProgramOptions] = useState<any[]>([]);
    const [isLoadingPrograms, setIsLoadingPrograms] = useState(false);
    const [timeSpans, setTimeSpans] = useState<string[]>([]);
    const [mainProgramId, setMainProgramId] = useState("");
    const [mainProgramPage, setMainProgramPage] = useState(1);
    const [mainProgramOptions, setMainProgramOptions] = useState<any[]>([]);
    const [mainProgramSearch, setMainProgramSearch] = useState("");
    const [debouncedMainProgramSearch, setDebouncedMainProgramSearch] = useState("");
    const [hasMoreMainPrograms, setHasMoreMainPrograms] = useState(true);
    const [isLoadingMainPrograms, setIsLoadingMainPrograms] = useState(false);
    const [initialMainProgramOption, setInitialMainProgramOption] = useState<any | null>(null);


    const resolveMuxVideoUrl = async (id: string) => {
        const trimmedPlaybackId = id.trim();
        if (!trimmedPlaybackId) {
            return "";
        }

        const muxTokenRes = await workoutService.getMuxToken(trimmedPlaybackId);
        const muxToken =
            muxTokenRes?.data?.token ||
            muxTokenRes?.data?.muxToken ||
            muxTokenRes?.data?.data?.token ||
            muxTokenRes?.data?.data?.muxToken ||
            muxTokenRes?.token ||
            muxTokenRes?.muxToken;

        if (!muxToken) {
            throw new Error("MUX token not found in response");
        }

        return `https://stream.mux.com/${trimmedPlaybackId}.m3u8?token=${muxToken}`;
    };

    const validateGeneralTab = () => {
        const newErrors: Record<string, string> = {};
        if (!image && !imageFile) newErrors.thumbnail = "Thumbnail is required";
        if (!title.trim()) newErrors.title = "Title is required";
        if (!durationEnum) newErrors.duration = "Duration is required";
        // if (!categoryId) newErrors.category = "Category is required";
        // if (!subCategoryId) newErrors.subCategory = "Sub-category is required";
        if (!difficultyLevel.length) newErrors.difficulty = "Difficulty level is required";
        if (!muscleGroup.length) newErrors.muscleGroup = "Select at least one muscle group";
        if (!equipment.length) newErrors.equipment = "Select at least one equipment";
        if (!trainingStyle.length) newErrors.trainingStyle = "Select at least one training style";
        if (!goalOutcome.length) newErrors.goalOutcome = "Select at least one goal outcome";
        if (!shortDescription.trim()) newErrors.shortDescription = "Short description is required";
        if (!(description || "").replace(/<[^>]*>/g, "").trim()) newErrors.description = "Description is required";

        const tMins = Number(durationMins) || 0;
        const tSecs = Number(durationSecs) || 0;
        if (tMins <= 0 && tSecs <= 0) {
            newErrors.durationInMins = "Video duration is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        // Block navigation to next tabs if general tab is invalid
        if (tabIndex === 0 && newValue > 0) {
            const valid = validateGeneralTab();
            if (!valid) {
                event.preventDefault();
                return;
            }
        }
        setTabIndex(newValue);
    };

    // Fetch settings-based dropdowns
    const equipmentParams = useMemo(() => ({ type: WORKOUT_ATTRIBUTES.EQUIPMENT }), []);
    const muscleParams = useMemo(() => ({ type: WORKOUT_ATTRIBUTES.MUSCLE_GROUP }), []);
    const trainingStyleParams = useMemo(() => ({ type: WORKOUT_ATTRIBUTES.TRAINING_STYLE }), []);
    const goalOutcomeParams = useMemo(() => ({ type: WORKOUT_ATTRIBUTES.GOAL_OUTCOME }), []);
    const categoryParams = useMemo(() => ({ page: 1, limit: 100 }), []);
    const subCategoryParams = useMemo(() => ({ page: 1, limit: 100 }), []);

    useEffect(() => {
        const t = setTimeout(() => {
            if (programSearch !== debouncedProgramSearch) {
                setDebouncedProgramSearch(programSearch);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [programSearch]);

    useEffect(() => {
        const fetchPrograms = async () => {
            setIsLoadingPrograms(true);
            try {
                const res = await workoutService.getPrograms({
                    page: 1,
                    limit: 100,
                    search: debouncedProgramSearch || undefined,
                });
                const raw = res?.data;
                let list: any[] = [];
                if (Array.isArray(raw)) {
                    list = raw;
                } else if (raw && typeof raw === "object") {
                    if (Array.isArray((raw as any).list)) list = (raw as any).list;
                    else if (Array.isArray((raw as any).data)) list = (raw as any).data;
                }
                setProgramOptions(list);
            } catch (err) {
                console.error("Failed to fetch programs", err);
            } finally {
                setIsLoadingPrograms(false);
            }
        };
        fetchPrograms();
    }, [debouncedProgramSearch]);

    // Resolve programRefId string IDs → selectedProgramsData using loaded options
    useEffect(() => {
        if (programRefId.length === 0) return;
        const missingIds = programRefId.filter(
            (id) => !selectedProgramsData.some((p) => p._id === id)
        );
        if (missingIds.length === 0) return;
        const resolved = missingIds
            .map((id) => programOptions.find((p: any) => p._id === id))
            .filter(Boolean)
            .map((p: any) => ({ _id: p._id, title: p.title ?? p.name ?? "" }));
        if (resolved.length > 0) {
            setSelectedProgramsData((prev) => [...prev, ...resolved]);
        }
    }, [programRefId, programOptions]);

    // Infinite scroll for Main Program
    useEffect(() => {
        const t = setTimeout(() => {
            if (mainProgramSearch !== debouncedMainProgramSearch) {
                setDebouncedMainProgramSearch(mainProgramSearch);
                setMainProgramPage(1);
                setMainProgramOptions([]);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [mainProgramSearch]);

    useEffect(() => {
        const fetchMainPrograms = async () => {
            setIsLoadingMainPrograms(true);
            try {
                const res = await workoutService.getPrograms({
                    page: mainProgramPage,
                    limit: 20,
                    search: debouncedMainProgramSearch || undefined
                });
                const raw = res?.data;
                let list: any[] = [];
                let total = 0;
                if (Array.isArray(raw)) {
                    list = raw;
                    total = raw.length;
                } else if (raw && typeof raw === "object") {
                    if (Array.isArray((raw as any).list)) {
                        list = (raw as any).list;
                        total = (raw as any).totalCount || list.length;
                    } else if (Array.isArray((raw as any).data)) {
                        list = (raw as any).data;
                        total = (raw as any).totalCount || list.length;
                    }
                }

                if (mainProgramPage === 1) {
                    setMainProgramOptions(list);
                } else {
                    setMainProgramOptions(prev => [...prev, ...list]);
                }
                setHasMoreMainPrograms(mainProgramOptions.length + list.length < total);
            } catch (err) {
                console.error("Failed to fetch main programs", err);
            } finally {
                setIsLoadingMainPrograms(false);
            }
        };
        fetchMainPrograms();
    }, [mainProgramPage, debouncedMainProgramSearch]);

    const loadMoreMainPrograms = () => {
        if (!isLoadingMainPrograms && hasMoreMainPrograms) {
            setMainProgramPage(prev => prev + 1);
        }
    };


    const { data: equipmentData } = useGetSettings(equipmentParams, { enabled: true });
    const { data: muscleData } = useGetSettings(muscleParams, { enabled: true });
    const { data: trainingStyleData } = useGetSettings(trainingStyleParams, { enabled: true });
    const { data: goalOutcomeData } = useGetSettings(goalOutcomeParams, { enabled: true });

    const { data: categoriesData } = useGetCategories(categoryParams);
    const { data: subCategoriesData } = useGetCategories(subCategoryParams);

    const normalizeList = (raw: any) => {
        if (Array.isArray(raw)) return raw;
        if (raw && typeof raw === "object") {
            if (Array.isArray(raw.list)) return raw.list;
            if (Array.isArray(raw.data)) return raw.data;
        }
        return [];
    };

    const equipmentOptions = useMemo(() => normalizeList(equipmentData?.data), [equipmentData]);
    const muscleOptions = useMemo(() => normalizeList(muscleData?.data), [muscleData]);
    const trainingStyleOptions = useMemo(() => normalizeList(trainingStyleData?.data), [trainingStyleData]);
    const goalOutcomeOptions = useMemo(() => normalizeList(goalOutcomeData?.data), [goalOutcomeData]);

    const categoryOptions = useMemo(
        () => normalizeList(categoriesData?.data).filter((c: any) => (c as { type?: number }).type === 1),
        [categoriesData]
    );
    const subCategoryOptions = useMemo(
        () =>
            normalizeList(subCategoriesData?.data).filter(
                (c: any) => (c as { type?: number }).type === 2 && (!categoryId || (c as { parentId?: string }).parentId === categoryId)
            ),
        [subCategoriesData, categoryId]
    );


    const equipmentOptionsMap = useMemo(
        () => equipmentOptions.reduce((acc: Record<string, string>, item: any) => ({ ...acc, [item._id]: item.name }), {}),
        [equipmentOptions]
    );
    const muscleOptionsMap = useMemo(
        () => muscleOptions.reduce((acc: Record<string, string>, item: any) => ({ ...acc, [item._id]: item.name }), {}),
        [muscleOptions]
    );
    const trainingStyleOptionsMap = useMemo(
        () => trainingStyleOptions.reduce((acc: Record<string, string>, item: any) => ({ ...acc, [item._id]: item.name }), {}),
        [trainingStyleOptions]
    );
    const goalOutcomeOptionsMap = useMemo(
        () => goalOutcomeOptions.reduce((acc: Record<string, string>, item: any) => ({ ...acc, [item._id]: item.name }), {}),
        [goalOutcomeOptions]
    );

    const { mutateAsync: createWorkout } = useCreateWorkout();
    const { mutateAsync: updateWorkout } = useUpdateWorkout();
    const {
        data: workoutDetail,
        isLoading: isLoadingWorkout,
        refetch: refetchWorkout,
    } = useGetWorkout(sourceWorkoutId, { enabled: isEditMode || isDuplicateMode });

    useEffect(() => {
        if ((isEditMode || isDuplicateMode) && workoutDetail?.data) {
            const w = workoutDetail.data as any;
            setImage(w.thumnail || "");
            setTitle(w.title || "");
            setRefCode(isDuplicateMode ? "" : w.refCode || "");
            const durationValue = w.duration ? Number(w.duration) : 0;
            setDurationEnum(durationValue ? String(durationValue) : "");
            setCategoryId(w.categoryId?._id || w.categoryId || "");
            setSubCategoryId(w.subCategoryId?._id || w.subCategoryId || "");
            setDifficultyLevel(
                Array.isArray(w.difficultyLevel)
                    ? w.difficultyLevel
                        .map((d: any) => normalizeDifficultyCode(d))
                        .filter(Boolean) as string[]
                    : w.difficultyLevel
                        ? [normalizeDifficultyCode(w.difficultyLevel)].filter(Boolean) as string[]
                        : []
            );
            setVisibilityScope(w.visibilityScope ? String(w.visibilityScope) : "");
            setEquipment(
                Array.isArray(w.equipment)
                    ? w.equipment.map((e: any) => e?._id || e).filter(Boolean)
                    : []
            );
            setMuscleGroup(
                Array.isArray(w.muscleGroup)
                    ? w.muscleGroup.map((m: any) => m?._id || m).filter(Boolean)
                    : []
            );
            setTrainingStyle(
                Array.isArray(w.trainingStyle)
                    ? w.trainingStyle.map((t: any) => t?._id || t).filter(Boolean)
                    : []
            );
            setGoalOutcome(
                Array.isArray(w.goalOutcome)
                    ? w.goalOutcome.map((g: any) => g?._id || g).filter(Boolean)
                    : []
            );
            if (Array.isArray(w.programRefId)) {
                const ids = w.programRefId.map((p: any) => (typeof p === "object" && p?._id ? p._id : p)).filter(Boolean);
                setProgramRefId(ids);
                const populated = w.programRefId.filter((p: any) => typeof p === "object" && p?._id);
                const mapped = populated.map((p: any) => ({ _id: p._id, title: p.title ?? p.name ?? "" }));
                setSelectedProgramsData(mapped);
            } else {
                setProgramRefId([]);
                setSelectedProgramsData([]);
            }
            setShortDescription(w.shortDescription || "");
            setDescription(w.description || "");
            const existingVideoValue = w.videoUrl || "";
            const existingPlaybackId = extractMuxPlaybackId(existingVideoValue);
            setPlaybackId(existingPlaybackId);
            // In edit mode API may return plain playbackId. Keep preview URL empty until tokenized URL is resolved.
            setVideoUrl(existingVideoValue.includes("stream.mux.com/") ? existingVideoValue : "");

            const totalSecs = Number(w.durationInMins) || 0;
            if (totalSecs > 0) {
                setDurationMins(String(Math.floor(totalSecs / 60)));
                setDurationSecs(String(totalSecs % 60));
            } else {
                setDurationMins("");
                setDurationSecs("");
            }

            setProductLinks(Array.isArray(w.productLinks) ? w.productLinks : []);
            const exercisesSource = w.excercise ?? w.exercises;
            if (Array.isArray(exercisesSource)) {
                setExerciseContent(
                    exercisesSource
                        .map((ex: any) => (typeof ex === "string" ? ex : ex?.name || ex?._id || ""))
                        .filter(Boolean)
                        .join("<br />")
                );
            } else if (typeof exercisesSource === "string") {
                setExerciseContent(exercisesSource);
            } else {
                setExerciseContent("");
            }
            setIsFeatured(!!w.isFeatured);
            setIsPublished(isDuplicateMode ? false : !!w.isPublished);
            const rawBeepSecs = Array.isArray(w.beepSecs) ? w.beepSecs : [];
            const formattedTimeSpans = rawBeepSecs.map((s: number) => {
                const mins = Math.floor(s / 60);
                const secs = s % 60;
                return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
            });
            setTimeSpans(formattedTimeSpans);
            setMainProgramId(w.mainProgramId?._id || w.mainProgramId?.id || (typeof w.mainProgramId === "string" ? w.mainProgramId : ""));
            if (w.mainProgramId && typeof w.mainProgramId === "object") {
                setInitialMainProgramOption(w.mainProgramId);
            }
            setErrors({});
        }

    }, [isEditMode, isDuplicateMode, workoutDetail]);

    useEffect(() => {
        const shouldResolveVideoUrl =
            !!playbackId.trim() &&
            (!videoUrl.trim() || videoUrl.trim() === playbackId.trim() || !videoUrl.includes("stream.mux.com/"));

        if (!shouldResolveVideoUrl) return;

        const hydrateVideoUrlFromPlaybackId = async () => {
            try {
                const resolvedVideoUrl = await resolveMuxVideoUrl(playbackId);
                setVideoUrl(resolvedVideoUrl);
            } catch (err) {
                console.error("Failed to resolve MUX URL for preview", err);
            }
        };

        hydrateVideoUrlFromPlaybackId();
    }, [playbackId]);

    const addProductLink = () => {
        setProductLinks([...productLinks, { name: "", url: "" }]);
    };

    const removeProductLink = (index: number) => {
        setProductLinks(productLinks.filter((_, i) => i !== index));
    };

    const updateProductLink = (index: number, field: "name" | "url", value: string) => {
        const updated = [...productLinks];
        updated[index] = { ...updated[index], [field]: value };
        setProductLinks(updated);
    };

    const handleSubmit = async () => {
        const newErrors: Record<string, string> = {};

        if (!image && !imageFile) newErrors.thumbnail = "Thumbnail is required";
        if (!title.trim()) newErrors.title = "Title is required";
        if (!durationEnum) newErrors.duration = "Duration is required";
        // if (!categoryId) newErrors.category = "Category is required";
        // if (!subCategoryId) newErrors.subCategory = "Sub-category is required";
        if (!difficultyLevel.length) newErrors.difficulty = "Difficulty level is required";
        if (!visibilityScope) newErrors.visibilityScope = "Visibility scope is required";
        if (!muscleGroup.length) newErrors.muscleGroup = "Select at least one muscle group";
        if (!equipment.length) newErrors.equipment = "Select at least one equipment";
        if (!trainingStyle.length) newErrors.trainingStyle = "Select at least one training style";
        if (!goalOutcome.length) newErrors.goalOutcome = "Select at least one goal outcome";
        if (!shortDescription.trim()) newErrors.shortDescription = "Short description is required";
        if (!(description || "").replace(/<[^>]*>/g, "").trim()) newErrors.description = "Description is required";
        if (!playbackId.trim()) newErrors.videoUrl = "MUX Playback ID is required";

        const tMinsSubmit = Number(durationMins) || 0;
        const tSecsSubmit = Number(durationSecs) || 0;
        if (tMinsSubmit <= 0 && tSecsSubmit <= 0) {
            newErrors.durationInMins = "Video duration is required";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            showError("Please fill all required fields");
            return;
        }

        let thumbnailUrl = image;
        if (imageFile) {
            try {
                setIsSubmitting(true);
                const fd = new FormData();
                fd.append("file", imageFile);
                const uploadRes = await uploadService.uploadFile(fd);
                if (typeof uploadRes?.data === "string") thumbnailUrl = uploadRes.data;
                else if (uploadRes?.data?.url) thumbnailUrl = (uploadRes.data as any).url;
            } catch (err: any) {
                setIsSubmitting(false);
                showError(err?.response?.data?.message || "Failed to upload thumbnail");
                return;
            } finally {
                setIsSubmitting(false);
            }
        }

        setIsSubmitting(true);
        try {
            await resolveMuxVideoUrl(playbackId);

            const payload = {
                thumnail: thumbnailUrl || "",
                title: title.trim(),
                refCode: refCode.trim(),
                duration: Number(durationEnum),
                // categoryId,
                // subCategoryId,
                difficultyLevel: difficultyLevel.map((d) => Number(d)),
                visibilityScope: Number(visibilityScope),
                equipment,
                muscleGroup,
                trainingStyle,
                goalOutcome,
                shortDescription: shortDescription.trim(),
                description: description.trim(),
                videoUrl: playbackId.trim(),
                durationInMins: (Number(durationMins) || 0) * 60 + (Number(durationSecs) || 0),
                productLinks: productLinks.filter(link => link.name.trim() && link.url.trim()),
                excercise: exerciseContent.trim(),
                isFeatured,
                isPublished,
                beepSecs: timeSpans.map(ts => {
                    const parts = ts.split(':');
                    if (parts.length === 2) {
                        return (Number(parts[0]) || 0) * 60 + (Number(parts[1]) || 0);
                    }
                    if (parts.length === 1) {
                        return Number(parts[0]) || 0;
                    }
                    return 0;
                }),
                programRefId: programRefId.length ? programRefId : undefined,
                mainProgramId: mainProgramId || undefined,
            };


            if (isEditMode) {
                await updateWorkout({ id: workoutId, data: payload });
                await refetchWorkout();
                showSuccess("Workout updated successfully");
            } else {
                await createWorkout(payload);
                showSuccess("Workout created successfully");
                navigate("/manage-workout");
            }
        } catch (err: any) {
            showError(err?.response?.data?.message || `Failed to ${isEditMode ? "update" : "create"} workout`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="main_title">
                <h1>{isEditMode ? "Edit Workout" : isDuplicateMode ? "Duplicate Workout" : "Add Workout"}</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/manage-workout")}>
                        {/* Manage Workout */}
                    </button>{" "}
                    - {isEditMode ? "Edit Workout" : isDuplicateMode ? "Duplicate Workout" : "Add Workout"}
                </p>
            </div>

            <Box className="custom_tabs1">
                <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    aria-label="Workout Tabs"
                    textColor="primary"
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    className="custom_tabs_links"
                >
                    <Tab label="General Details" />
                    <Tab label="Media" />
                    {/* <Tab label="Timer Mode" /> */}
                </Tabs>
            </Box>

            <div className="cards">

                {/* TAB 1: GENERAL DETAILS */}
                <TabPanel value={tabIndex} index={0}>
                    <div className="form">
                        <div className="gap_p">

                            {/* Upload Thumbnail */}
                            <div className="control_group w_33">
                                <label>Upload Thumbnail</label>

                                {image ? (
                                    <div className="upload_image v2 no_radius">
                                        <div className="upload_image_holder">
                                            <figure>
                                                <CardMedia component="img" image={image} alt="thumb" />
                                            </figure>
                                            <IconButton
                                                aria-label="Remove"
                                                onClick={() => {
                                                    setImage("");
                                                    setFileName("");
                                                    setImageFile(null);
                                                }}
                                                size="small"
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </div>
                                    </div>
                                ) : (
                                    <label className="upload_image v2 no_radius" htmlFor="fileUpload">
                                        <Input
                                            id="fileUpload"
                                            sx={{ display: "none" }}
                                            type="file"
                                            inputProps={{ accept: "image/png,image/jpeg" }}
                                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                const files = event.target.files;
                                                if (files && files[0].type.includes("image")) {
                                                    setFileName(files[0].name);
                                                    setImage(URL.createObjectURL(files[0]));
                                                    setImageFile(files[0]);
                                                    setErrors((prev) => {
                                                        const { thumbnail, ...rest } = prev;
                                                        return rest;
                                                    });
                                                }
                                            }}
                                        />
                                        <span className="upload_image_holder">
                                            <figure className="add_img">
                                                <img src="/static/images/placeholder.png" alt="placeholder" />
                                            </figure>
                                        </span>
                                    </label>
                                )}
                                {errors.thumbnail ? <p className="err_msg">{errors.thumbnail}</p> : null}
                            </div>

                            <div className="control_group w_33">
                                <label>Workout Title</label>
                                <TextField
                                    placeholder="Enter Workout Title"
                                    fullWidth
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                    error={!!errors.title}
                                    helperText={errors.title}
                                />
                            </div>

                            <div className="control_group w_33">
                                <label>Ref Code</label>
                                <TextField
                                    placeholder="Enter Ref Code"
                                    fullWidth
                                    value={refCode}
                                    onChange={(e) => setRefCode(e.target.value)}
                                />
                            </div>

                            {/* Duration */}
                            <div className="control_group w_33">
                                <label>Duration Tag</label>
                                <Select
                                    value={durationEnum || "def"}
                                    fullWidth
                                    displayEmpty
                                    onChange={(e) => {
                                        const enumValue = e.target.value as string;
                                        setDurationEnum(enumValue);
                                    }}
                                    error={!!errors.duration}
                                >
                                    <MenuItem value="def" disabled>Select Duration</MenuItem>
                                    <MenuItem value={String(WORKOUT_DURATION.TEN_MINS)}>5-10 Mins</MenuItem>
                                    <MenuItem value={String(WORKOUT_DURATION.TWENTY_MINS)}>10-20 Mins</MenuItem>
                                    <MenuItem value={String(WORKOUT_DURATION.THIRTY_MINS)}>20-30 Mins</MenuItem>
                                    <MenuItem value={String(WORKOUT_DURATION.FORTY_MINS)}>30-40 Mins</MenuItem>
                                    <MenuItem value={String(WORKOUT_DURATION.SIXTY_MINS)}>40+ Mins</MenuItem>
                                </Select>
                                {errors.duration ? <p className="err_msg">{errors.duration}</p> : null}
                            </div>

                            <div className="control_group w_33">
                                <label>Video Duration</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <TextField
                                        type="number"
                                        placeholder="Mins"
                                        fullWidth
                                        value={durationMins}
                                        onChange={(e) => {
                                            const numericValue = e.target.value.replace(/\D/g, "").slice(0, 4);
                                            setDurationMins(numericValue);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "-" || e.key === "+" || e.key.toLowerCase() === "e") {
                                                e.preventDefault();
                                            }
                                        }}
                                        required
                                        error={!!errors.durationInMins}
                                        inputProps={{ min: 0, step: 1, maxLength: 4 }}
                                    />
                                    <TextField
                                        type="number"
                                        placeholder="Secs"
                                        fullWidth
                                        value={durationSecs}
                                        onChange={(e) => {
                                            const numericValue = e.target.value.replace(/\D/g, "").slice(0, 2);
                                            setDurationSecs(numericValue);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "-" || e.key === "+" || e.key.toLowerCase() === "e") {
                                                e.preventDefault();
                                            }
                                        }}
                                        required
                                        error={!!errors.durationInMins}
                                        inputProps={{ min: 0, max: 59, step: 1, maxLength: 2 }}
                                    />
                                </div>
                                {errors.durationInMins ? <p className="err_msg" style={{ marginTop: '4px', fontSize: '0.75rem', color: '#d32f2f', paddingLeft: '14px' }}>{errors.durationInMins}</p> : null}
                            </div>

                            {/* Category */}
                            {/* <div className="control_group w_33">
                                <label>Category</label>
                                <Select
                                    value={categoryId || "def"}
                                    fullWidth
                                    onChange={(e) => {
                                        setCategoryId(e.target.value as string);
                                        setSubCategoryId("");
                                    }}
                                    error={!!errors.category}
                                >
                                    <MenuItem value="def" disabled>Select Category</MenuItem>
                                    {categoryOptions.map((cat: any) => (
                                        <MenuItem key={cat._id} value={cat._id}>
                                            {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.category ? <p className="err_msg">{errors.category}</p> : null}
                            </div> */}

                            {/* <div className="control_group w_33">
                                <label>Sub Category</label>
                                <Select
                                    value={subCategoryId || "def"}
                                    fullWidth
                                    onChange={(e) => setSubCategoryId(e.target.value as string)}
                                    disabled={!categoryId}
                                    error={!!errors.subCategory}
                                >
                                    <MenuItem value="def" disabled>Select Sub-Category</MenuItem>
                                    {subCategoryOptions.map((sub: any) => (
                                        <MenuItem key={sub._id} value={sub._id}>
                                            {sub.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.subCategory ? <p className="err_msg">{errors.subCategory}</p> : null}
                            </div> */}

                            {/* Level */}
                            <div className="control_group w_33">
                                <label>Level</label>
                                <Select
                                    multiple
                                    value={difficultyLevel}
                                    fullWidth
                                    displayEmpty
                                    onChange={(e) =>
                                        setDifficultyLevel(
                                            typeof e.target.value === "string"
                                                ? e.target.value.split(",")
                                                : (e.target.value as string[])
                                        )
                                    }
                                    renderValue={(selected) => {
                                        if (selected.length === 0) {
                                            return <span style={{ color: "#999" }}>Select Level</span>;
                                        }
                                        const labelMap: Record<string, string> = {
                                            [String(DIFFICULTY_LEVEL.BEGGINER)]: "Beginner",
                                            [String(DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE)]: "Beginner-Intermediate",
                                            [String(DIFFICULTY_LEVEL.INTERMEDIATE)]: "Intermediate",
                                            [String(DIFFICULTY_LEVEL.ADVANCED)]: "Advanced",
                                        };
                                        const labels = (selected as string[])
                                            .map((value) => labelMap[value])
                                            .filter(Boolean);
                                        return labels.length
                                            ? labels.join(", ")
                                            : <span style={{ color: "#999" }}>Select Level</span>;
                                    }}
                                    error={!!errors.difficulty}
                                >
                                    <MenuItem value={String(DIFFICULTY_LEVEL.BEGGINER)}>Beginner</MenuItem>
                                    <MenuItem value={String(DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE)}>Beginner-Intermediate</MenuItem>
                                    <MenuItem value={String(DIFFICULTY_LEVEL.INTERMEDIATE)}>Intermediate</MenuItem>
                                    <MenuItem value={String(DIFFICULTY_LEVEL.ADVANCED)}>Advanced</MenuItem>
                                </Select>
                                {errors.difficulty ? <p className="err_msg">{errors.difficulty}</p> : null}
                            </div>

                            {/* Muscle Group */}
                            <div className="control_group w_33">
                                <label>Muscle Group</label>
                                <Select
                                    multiple
                                    value={muscleGroup}
                                    fullWidth
                                    displayEmpty
                                    onChange={(e) => setMuscleGroup(typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[]))}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) {
                                            return <span style={{ color: '#999' }}>Select Muscle Group</span>;
                                        }
                                        return selected.map(id => muscleOptionsMap[id] || id).join(", ");
                                    }}
                                    error={!!errors.muscleGroup}
                                >
                                    {muscleOptions.map((item: any) => (
                                        <MenuItem key={item._id} value={item._id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.muscleGroup ? <p className="err_msg">{errors.muscleGroup}</p> : null}
                            </div>

                            <div className="control_group w_33">
                                <label>Equipment</label>
                                <Select
                                    multiple
                                    value={equipment}
                                    fullWidth
                                    displayEmpty
                                    onChange={(e) => setEquipment(typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[]))}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) {
                                            return <span style={{ color: '#999' }}>Select Equipment</span>;
                                        }
                                        return selected.map(id => equipmentOptionsMap[id] || id).join(", ");
                                    }}
                                    error={!!errors.equipment}
                                >
                                    {equipmentOptions.map((item: any) => (
                                        <MenuItem key={item._id} value={item._id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.equipment ? <p className="err_msg">{errors.equipment}</p> : null}
                            </div>

                            <div className="control_group w_33">
                                <label>Workout Type</label>
                                <Select
                                    multiple
                                    value={trainingStyle}
                                    fullWidth
                                    displayEmpty
                                    onChange={(e) => setTrainingStyle(typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[]))}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) {
                                            return <span style={{ color: '#999' }}>Select Workout Type</span>;
                                        }
                                        return selected.map(id => trainingStyleOptionsMap[id] || id).join(", ");
                                    }}
                                    error={!!errors.trainingStyle}
                                >
                                    {trainingStyleOptions.map((item: any) => (
                                        <MenuItem key={item._id} value={item._id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.trainingStyle ? <p className="err_msg">{errors.trainingStyle}</p> : null}
                            </div>

                            <div className="control_group w_33">
                                <label>Goal</label>
                                <Select
                                    multiple
                                    value={goalOutcome}
                                    fullWidth
                                    displayEmpty
                                    onChange={(e) => setGoalOutcome(typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[]))}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) {
                                            return <span style={{ color: '#999' }}>Select Goal</span>;
                                        }
                                        return selected.map(id => goalOutcomeOptionsMap[id] || id).join(", ");
                                    }}
                                    error={!!errors.goalOutcome}
                                >
                                    {goalOutcomeOptions.map((item: any) => (
                                        <MenuItem key={item._id} value={item._id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.goalOutcome ? <p className="err_msg">{errors.goalOutcome}</p> : null}
                            </div>

                            {/* Main Program (Single Searchable Dropdown with Infinite Scroll) */}
                            <div className="control_group w_100">
                                <label>Main Program</label>
                                <Autocomplete
                                    options={mainProgramOptions}
                                    value={
                                        mainProgramOptions.find((o: any) => o._id === mainProgramId) ||
                                        (initialMainProgramOption?._id === mainProgramId ? initialMainProgramOption : (mainProgramId === "" && initialMainProgramOption ? initialMainProgramOption : null)) ||
                                        null
                                    }
                                    onChange={(_, newValue: any) => {
                                        setMainProgramId(newValue?._id || "");
                                        if (newValue) {
                                            setInitialMainProgramOption(newValue);
                                        }
                                    }}
                                    getOptionLabel={(option: any) => option?.title || ""}
                                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                    onInputChange={(_, val) => setMainProgramSearch(val)}
                                    autoComplete
                                    includeInputInList
                                    filterOptions={(x) => x}
                                    loading={isLoadingMainPrograms}
                                    ListboxProps={{
                                        onScroll: (event: React.SyntheticEvent) => {
                                            const listboxNode = event.currentTarget as HTMLElement;
                                            if (
                                                listboxNode.scrollTop + listboxNode.clientHeight >=
                                                listboxNode.scrollHeight - 10
                                            ) {
                                                loadMoreMainPrograms();
                                            }
                                        },
                                    } as any}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search and select main program"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {isLoadingMainPrograms ? (
                                                            <CircularProgress color="inherit" size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </div>

                            {/* Programs (searchable multiselect) */}
                            <div className="control_group w_100">
                                <label>Programs</label>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={programOptions}
                                    value={selectedProgramsData}
                                    inputValue={programSearch}
                                    filterOptions={(x) => x}
                                    onChange={(_, newValue: any[]) => {
                                        setProgramRefId(newValue.map((p) => p._id));
                                        setSelectedProgramsData(newValue.map((p) => ({ _id: p._id, title: p.title ?? p.name ?? "" })));
                                    }}
                                    getOptionLabel={(option: any) => option?.title ?? ""}
                                    getOptionKey={(option: any) => option?._id ?? ""}
                                    isOptionEqualToValue={(option: any, value: any) => option?._id === value?._id}
                                    onInputChange={(_, val, reason) => {
                                        if (reason === "input" || reason === "clear") setProgramSearch(val);
                                    }}
                                    loading={isLoadingPrograms}
                                    renderOption={(props, option: any, { selected }) => (
                                        <li {...props}>
                                            <FormControlLabel
                                                control={<Checkbox checked={selected} />}
                                                label={option?.title ?? option?._id ?? ""}
                                            />
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search and select programs"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {isLoadingPrograms ? (
                                                            <CircularProgress color="inherit" size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </div>

                            {/* Description */}
                            <div className="control_group w_100">
                                <label>Short Description</label>
                                <TextField
                                    placeholder="Enter Short Description"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={shortDescription}
                                    onChange={(e) => setShortDescription(e.target.value)}
                                    required
                                    error={!!errors.shortDescription}
                                    helperText={errors.shortDescription}
                                />
                            </div>

                            <div className="control_group w_100">
                                <label>Description</label>
                                <EditText content={description} setContent={setDescription} />
                                {errors.description ? <p className="err_msg">{errors.description}</p> : null}
                            </div>

                        </div>

                        <div className="form_btn">
                            {isEditMode ? (
                                <Button
                                    variant="contained"
                                    color="success"
                                    className="btn btn_primary"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    sx={{ mr: 1 }}
                                >
                                    {isSubmitting ? "Saving..." : "Save"}
                                </Button>
                            ) : null}
                            <Button
                                variant="contained"
                                color="success"
                                className="btn btn_primary"
                                onClick={() => {
                                    if (validateGeneralTab()) {
                                        setTabIndex(1);
                                    }
                                }}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </TabPanel>

                {/* TAB 2: MEDIA */}
                <TabPanel value={tabIndex} index={1}>
                    <div className="form">
                        <div className="gap_p">

                            {/* Video Preview */}
                            <div className="control_group w_33">
                                <label>Upload standed video</label>

                                {videoUrl ? (
                                    <div className="upload_image v2 no_radius">
                                        <div className="upload_image_holder">
                                            <figure>
                                                <ReactPlayer
                                                    src={videoUrl}
                                                    controls
                                                    width="100%"
                                                    height="200px"
                                                    playsInline
                                                    style={{
                                                        width: "100%",
                                                        height: "200px",
                                                        borderRadius: "8px"
                                                    }}
                                                />
                                            </figure>
                                            <IconButton
                                                aria-label="Remove"
                                                onClick={() => {
                                                    setVideoUrl("");
                                                    setPlaybackId("");
                                                }}
                                                size="small"
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                    backgroundColor: "rgba(255, 255, 255, 0.8)"
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="upload_image v2 no_radius">
                                        <span className="upload_image_holder">
                                            <figure className="add_img">
                                                <img style={{ objectFit: "contain" }} src="/static/images/play2.jpg" alt="play2" />
                                            </figure>
                                        </span>
                                    </div>
                                )}
                            </div>

                        </div>

                        <Box className="control_group w_100" sx={{ mt: 3 }}>
                            <label>MUX Playback ID</label>
                            <TextField
                                placeholder="Enter MUX Playback ID"
                                fullWidth
                                value={playbackId}
                                onChange={(e) => {
                                    setPlaybackId(e.target.value);
                                    setVideoUrl("");
                                }}
                                onBlur={async () => {
                                    if (!playbackId.trim()) {
                                        return;
                                    }
                                    try {
                                        const resolvedVideoUrl = await resolveMuxVideoUrl(playbackId);
                                        setVideoUrl(resolvedVideoUrl);
                                    } catch (err: any) {
                                        showError(err?.response?.data?.message || err?.message || "Failed to generate MUX URL");
                                    }
                                }}
                                required
                                error={!!errors.videoUrl}
                                helperText={errors.videoUrl}
                            />
                        </Box>

                        <div className="control_group w_100">
                            <label>Time stamp</label>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={[]}
                                value={timeSpans}
                                onChange={(_, newValue) => {
                                    // Filter to ensure only numbers and colons are allowed
                                    const filtered = (newValue as string[]).map(v => v.replace(/[^0-9:]/g, '')).filter(Boolean);
                                    setTimeSpans(filtered);
                                }}
                                renderTags={() => null} // Hide tags inside to show them below
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Add time stamp in mins (e.g. 2:04, 5:10)"
                                        onKeyDown={(e) => {
                                            const isDigit = /[0-9]/.test(e.key);
                                            const isColon = e.key === ':';
                                            const isControl = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key);

                                            if (!isDigit && !isColon && !isControl) {
                                                e.preventDefault();
                                            }

                                            if (e.key === 'Enter') {
                                                // If someone types e.g. 124 it might be seconds or invalid mm:ss
                                                // We rely on onChange to clean up characters.
                                            }
                                        }}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (!/^[0-9:]*$/.test(val)) {
                                                e.target.value = val.replace(/[^0-9:]/g, '');
                                            }
                                        }}
                                    />
                                )}
                            />
                            {/* Display entered time stamps below the input field */}
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {timeSpans.map((ts, index) => (
                                    <Chip
                                        key={`${ts}-${index}`}
                                        label={ts}
                                        onDelete={() => {
                                            const updated = timeSpans.filter((_, i) => i !== index);
                                            setTimeSpans(updated);
                                        }}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                ))}
                            </Box>
                        </div>
                        <div className="control_group w_100">
                            <label>Product Links</label>
                            <Box sx={{ mb: 2 }}>
                                {productLinks.map((link, index) => (
                                    <Box key={index} sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}>
                                        <TextField
                                            placeholder="Product Name"
                                            fullWidth
                                            value={link.name}
                                            onChange={(e) => updateProductLink(index, "name", e.target.value)}
                                            size="small"
                                        />
                                        <TextField
                                            placeholder="URL Link"
                                            fullWidth
                                            value={link.url}
                                            onChange={(e) => updateProductLink(index, "url", e.target.value)}
                                            size="small"
                                        />
                                        <IconButton
                                            onClick={() => removeProductLink(index)}
                                            color="error"
                                            size="small"
                                            sx={{ mt: 0.5 }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={addProductLink}
                                    size="small"
                                >
                                    Add Product Link
                                </Button>
                            </Box>
                        </div>

                        <div className="control_group w_100">
                            <label>Exercises</label>
                            <EditText content={exerciseContent} setContent={setExerciseContent} />
                        </div>

                        <div className="control_group w_33">
                            <label>Visibility Scope</label>
                            <Select
                                value={visibilityScope || "def"}
                                fullWidth
                                displayEmpty
                                onChange={(e) => setVisibilityScope(e.target.value as string)}
                                error={!!errors.visibilityScope}
                            >
                                <MenuItem value="def" disabled>Select Visibility</MenuItem>
                                <MenuItem value={String(VISIBILITY_SCOPE.DEMAND)}>Demand</MenuItem>
                                <MenuItem value={String(VISIBILITY_SCOPE.PROGRAM)}>Program</MenuItem>
                                <MenuItem value={String(VISIBILITY_SCOPE.BOTH)}>Both</MenuItem>
                            </Select>
                            {errors.visibilityScope ? <p className="err_msg">{errors.visibilityScope}</p> : null}
                        </div>

                        <div className="control_group w_33">
                            <label>Featured</label>
                            <Select
                                value={isFeatured ? "true" : "false"}
                                fullWidth
                                onChange={(e) => setIsFeatured(e.target.value === "true")}
                            >
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </Select>
                        </div>

                        <div className="control_group w_33">
                            <label>Publish</label>
                            <Select
                                value={isPublished ? "true" : "false"}
                                fullWidth
                                onChange={(e) => setIsPublished(e.target.value === "true")}
                            >
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </Select>
                        </div>

                        <div className="form_btn">
                            <Button
                                variant="contained"
                                color="success"
                                className="btn btn_primary"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Saving..." : isEditMode ? "Save" : "Submit"}
                            </Button>
                        </div>
                    </div>
                </TabPanel>

                {/* TAB 3: TIMER MODE */}
                {/* <TabPanel value={tabIndex} index={2}>
                    <div className="form">
                        <div className="gap_p">

                            <div className="control_group w_33">
                                <label>Start Time</label>
                                <TextField type="number" placeholder="Enter Start Time" fullWidth />
                            </div>

                            <div className="control_group w_33">
                                <label>Rest Timer</label>
                                <TextField type="number" placeholder="Enter Rest Time" fullWidth />
                            </div>

                            <div className="control_group w_33">
                                <label>Next/End Timer</label>
                                <TextField type="number" placeholder="Enter Next/End Time" fullWidth />
                            </div>

                        </div>

                        <div className="form_btn">
                            <Button variant="contained" color="success" className="btn btn_primary">
                                Save Workout
                            </Button>
                        </div>
                    </div>
                </TabPanel> */}

            </div>
        </>
    );
};

export default EditWorkout;
